import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core"
import { Field, Form, Formik } from "formik"
import { TextField } from "formik-material-ui"
import { useContext, useState } from "react"
import { useMutation } from "react-query"
import * as Yup from "yup"
import { createAppUser } from "~/actions/users"
import LoaderButton from "~/components/LoaderButton/index.jsx"
import AppContext from "~/contexts/App"
import useOpenErrorSnackbar from "~/hooks/useOpenErrorSnackbar"

import Box from "~/components/Common/Box"
import useStyles from "./styles"

const CreateAppUserDialog = () => {
  const classes = useStyles()
  const { dialogs, closeDialog, openSnackBar } = useContext(AppContext)
  const thisDialog = dialogs?.["createAppUserDialog"] || {}
  const { open = false, data } = thisDialog
  const [showPassword, setShowPassword] = useState(false)
  const [createdUser, setCreatedUser] = useState({})
  const openErrorSnackbar = useOpenErrorSnackbar()

  const createUserMutation = useMutation(createAppUser)

  const schema = Yup.object().shape({
    first_name: Yup.string().required("A first name is required"),
    last_name: Yup.string().required("A last name is required"),
    email: Yup.string()
      .required("An email is required")
      .email("Must be a valid email"),
    username: Yup.string()
      .required("A username is required")
      .min(3)
      .matches(
        /^[aA-zZ0-9-_]+$/,
        "username can only contain numbers, letters, -, _",
      ),
  })

  const handleClose = () => {
    closeDialog("createAppUserDialog", false)

    setTimeout(() => {
      // reset state
      setShowPassword(false)
      setCreatedUser({})
    }, 300)
  }

  const onSubmit = (values, actions) => {
    createUserMutation.mutate(values, {
      onSuccess: (res) => {
        openSnackBar({ message: `${res.message}` })

        // modal second step
        setShowPassword(true)
        setCreatedUser({
          name: `${res.user.first_name} ${res.user.last_name}`,
          email: res.user.email,
          password: res.password,
        })
        actions.setSubmitting(false)
        actions.resetForm()
      },
      onError: (err) => {
        actions.setSubmitting(false)
        openErrorSnackbar(err, "Error Creating User")
      },
    })
  }

  return (
    <Dialog maxWidth="xs" open={open}>
      <DialogTitle className={classes.dialogTitle} disableTypography>
        {!showPassword && (
          <>
            <Typography variant="h2" className={classes.title}>
              Create User
            </Typography>
            <Typography color="primary" variant="subtitle2">
              Create a new app user
            </Typography>
          </>
        )}
        {showPassword && (
          <>
            <Typography variant="h2" className={classes.title}>
              Account Info
            </Typography>
            <Typography color="primary" variant="subtitle2">
              {`Make sure to copy ${createdUser.name}'s account information now.  You won't be able to see it again!`}
            </Typography>
          </>
        )}
      </DialogTitle>
      <Formik
        initialValues={{
          first_name: "",
          last_name: "",
          email: "",
          username: "",
        }}
        onSubmit={onSubmit}
        validationSchema={schema}
      >
        {({ values, isSubmitting }) => (
          <Form>
            <DialogContent>
              {!showPassword && (
                <>
                  <Box>
                    <Typography
                      color="secondary"
                      className={classes.inputLabel}
                    >
                      First Name
                    </Typography>
                    <Field
                      name="first_name"
                      component={TextField}
                      variant="filled"
                      size="small"
                      fullWidth
                      InputProps={{
                        classes: {
                          root: classes.inputBackgroundColor,
                        },
                      }}
                    />
                  </Box>
                  <Box marginTop="16px">
                    <Typography
                      color="secondary"
                      className={classes.inputLabel}
                    >
                      Last Name
                    </Typography>
                    <Field
                      name="last_name"
                      component={TextField}
                      variant="filled"
                      size="small"
                      fullWidth
                      InputProps={{
                        classes: {
                          root: classes.inputBackgroundColor,
                        },
                      }}
                    />
                  </Box>
                  <Box marginTop="16px">
                    <Typography
                      color="secondary"
                      className={classes.inputLabel}
                    >
                      Email
                    </Typography>
                    <Field
                      name="email"
                      component={TextField}
                      variant="filled"
                      size="small"
                      fullWidth
                      InputProps={{
                        classes: {
                          root: classes.inputBackgroundColor,
                        },
                      }}
                    />
                  </Box>
                  <Box marginTop="16px">
                    <Typography
                      color="secondary"
                      className={classes.inputLabel}
                    >
                      Username
                    </Typography>
                    <Field
                      name="username"
                      component={TextField}
                      variant="filled"
                      size="small"
                      fullWidth
                      InputProps={{
                        classes: {
                          root: classes.inputBackgroundColor,
                        },
                      }}
                    />
                  </Box>
                </>
              )}
              {showPassword && (
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Box
                    padding="16px"
                    marginBottom="16px"
                    minWidth="75%"
                    maxWidth="100%"
                    style={{ backgroundColor: "rgb(245, 245, 245)" }}
                  >
                    <Typography variant="h3" align="center">
                      {createdUser.email}
                    </Typography>
                  </Box>
                  <Box
                    width="75%"
                    p={2}
                    style={{ backgroundColor: "rgb(245, 245, 245)" }}
                  >
                    <Typography variant="h3" align="center">
                      {createdUser.password}
                    </Typography>
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Box>
                <Button
                  onClick={handleClose}
                  style={{ height: "40px", marginRight: "8px" }}
                >
                  {showPassword ? "Close" : "Cancel"}
                </Button>
                {!showPassword && (
                  <LoaderButton
                    type="submit"
                    classoverrides={{
                      wrapper: classes.button,
                      button: {
                        root: classes.buttonRoot,
                      },
                    }}
                    variant="contained"
                    color="secondary"
                    working={isSubmitting}
                    size="small"
                  >
                    Create
                  </LoaderButton>
                )}
              </Box>
            </DialogActions>
          </Form>
        )}
      </Formik>
    </Dialog>
  )
}

CreateAppUserDialog.propTypes = {}

export default CreateAppUserDialog
